import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import Snackbar from '@sorare/core/src/atoms/snackbar/Snackbar';
import {
  HeadlineL,
  HeadlineS,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { StrokedText } from '@sorare/core/src/atoms/typography/StrokedText';
import Dialog from '@sorare/core/src/components/dialog';
import { FOOTBALL_PLAY_RIVALS_SQUADS_INVITE_JOINSECRET_USERSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useCopyToClipboard from '@sorare/core/src/hooks/useCopyToClipboard';
import { glossary } from '@sorare/core/src/lib/glossary';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';
import { BackgroundAligned } from '@sorare/gameplay/src/components/rivals/GridBackground/BackgroundAligned';

import { BrandedPlayButton } from 'components/rivals/BrandedPlayButton';
import { BrandedPlayButtonStatus } from 'components/rivals/BrandedPlayButton/types';

import { VisibilitySettings } from './VisibilitySettings';
import { InviteDialog_userGroupInterface } from './__generated__/index.graphql';

const Root = styled.div`
  padding: 0 var(--double-unit) var(--double-unit);
`;
const StyledImg = styled.img`
  width: 80px;
`;
const FooterWrapper = styled(Vertical)`
  padding: var(--double-unit);
`;
const ButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--intermediate-unit);
`;
const CodeTypography = styled(HeadlineL).attrs({ as: 'span' })`
  text-transform: uppercase;
  letter-spacing: 0.5ch;
`;

const inviteFriendsMessage = defineMessage({
  id: 'Squad.InviteDialog.inviteFriends',
  defaultMessage: 'Invite Friends',
});

type Props = {
  open: boolean;
  onClose: () => void;
  userGroup: InviteDialog_userGroupInterface;
};
export const InviteDialog = ({ open, onClose, userGroup }: Props) => {
  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const copyToClipboard = useCopyToClipboard();
  const { formatMessage } = useIntl();
  const { currentUser } = useCurrentUserContext();
  const { logo, displayName, joinSecret } = userGroup;

  if (!currentUser) return null;

  const pictureUrl = logo?.pictureUrl;

  const onCopyCode = () => {
    copyToClipboard(joinSecret);
    setCopiedSnackbarOpen(true);
  };

  const onCopyLink = () => {
    copyToClipboard(
      `${formatMessage({
        id: 'Squad.InviteDialog.inviteMessage',
        defaultMessage: 'Join my Squad on Sorare!',
      })} ${window.origin}${generatePath(
        FOOTBALL_PLAY_RIVALS_SQUADS_INVITE_JOINSECRET_USERSLUG,
        {
          joinSecret,
          userSlug: currentUser.slug,
        }
      )}`
    );
    setCopiedSnackbarOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        footer={
          <FooterWrapper>
            <Button size="large" onClick={onClose}>
              <FormattedMessage
                id="Squad.InviteDialog.primaryCta"
                defaultMessage="Complete setup"
              />
            </Button>
          </FooterWrapper>
        }
      >
        <Root>
          <Vertical gap={5}>
            <Vertical gap={0}>
              <HeadlineL>
                <FormattedMessage {...inviteFriendsMessage} />
              </HeadlineL>
              <GridBackground
                height={208}
                color="#454545"
                intersectionOnCenter
                bgColor="var(--c-neutral-200)"
              >
                <LabelL>
                  <FormattedMessage
                    id="Squad.InviteDialog.description"
                    defaultMessage="You need at least 2 members to be eligible for rewards."
                  />
                </LabelL>
                {pictureUrl && (
                  <BackgroundAligned>
                    <Vertical gap={2} center>
                      <StyledImg src={pictureUrl} alt="" />
                      <HeadlineL>{displayName}</HeadlineL>
                    </Vertical>
                  </BackgroundAligned>
                )}
              </GridBackground>
            </Vertical>
            <Vertical gap={2}>
              <Vertical gap={1.5}>
                <HeadlineS>
                  <FormattedMessage
                    id="Squad.InviteDialog.yourSquadCode"
                    defaultMessage="Your Squad Code"
                  />
                </HeadlineS>
                <BrandedPlayButton
                  status={BrandedPlayButtonStatus.GREEN}
                  onClick={onCopyCode}
                >
                  <CodeTypography>
                    <StrokedText strokeWidth={2} yOffset={2}>
                      {joinSecret}
                    </StrokedText>
                  </CodeTypography>
                </BrandedPlayButton>
              </Vertical>
              <ButtonsWrapper>
                <VisibilitySettings userGroup={userGroup} />
                <Button color="secondary" size="medium" onClick={onCopyLink}>
                  <FormattedMessage {...inviteFriendsMessage} />
                </Button>
              </ButtonsWrapper>
            </Vertical>
          </Vertical>
        </Root>
      </Dialog>

      <Snackbar
        autoHideDuration={2000}
        open={copiedSnackbarOpen}
        onClose={() => {
          setCopiedSnackbarOpen(false);
        }}
        message={formatMessage(glossary.copiedToClipboard)}
      />
    </>
  );
};

InviteDialog.fragments = {
  userGroupInterface: gql`
    fragment InviteDialog_userGroupInterface on UserGroupInterface {
      slug
      joinSecret
      publiclyAccessible
      displayName
      logo {
        id
        pictureUrl
      }
      ...VisibilitySettings_userGroupInterface
    }
    ${VisibilitySettings.fragments.userGroupInterface}
  ` as TypedDocumentNode<InviteDialog_userGroupInterface>,
};
